import { QaPreviewProject } from '@graphql/queries/QaPreviewProjectsQuery';

export const STANDALONE = 'standalone';

export interface QaPreviewUrlState {
  /** Explicit project id, STANDALONE, or null when not chosen yet */
  project: number | typeof STANDALONE | null;
  tab?: number;
  /** Preview as map only; undefined falls back to the report's own setting */
  mapOnly?: boolean;
}

export const parsePreviewUrlState = (search: string): QaPreviewUrlState => {
  const query = new URLSearchParams(search);
  const project = query.get('project');
  const projectId = parseInt(project ?? '', 10);
  const tab = parseInt(query.get('tab') ?? '', 10);

  return {
    project:
      project === STANDALONE
        ? STANDALONE
        : Number.isFinite(projectId) && projectId > 0
          ? projectId
          : null,
    tab: Number.isFinite(tab) ? tab : undefined,
    mapOnly: query.has('mapOnly') ? query.get('mapOnly') === '1' : undefined,
  };
};

export const serializePreviewUrlState = (state: QaPreviewUrlState): string => {
  const query = new URLSearchParams();
  if (state.project !== null) query.set('project', String(state.project));
  if (state.tab !== undefined) query.set('tab', String(state.tab));
  if (state.mapOnly !== undefined) {
    query.set('mapOnly', state.mapOnly ? '1' : '0');
  }
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
};

/**
 * Pick the project a reviewer most likely wants to preview the report in:
 * one that already contains it, else one whose name matches the report's
 * project name, else none (standalone).
 */
export const suggestPreviewProject = (
  projects: QaPreviewProject[],
  reportProjectName: string | null | undefined,
): QaPreviewProject | null => {
  const containing = projects.find((p) => p.containsReport.totalCount > 0);
  if (containing) return containing;

  const target = (reportProjectName ?? '').trim().toLowerCase();
  if (!target) return null;
  return projects.find((p) => p.name.trim().toLowerCase() === target) ?? null;
};

/** Resolve the project id to preview in (null = standalone). */
export const resolvePreviewProjectId = (
  urlProject: QaPreviewUrlState['project'],
  projects: QaPreviewProject[],
  reportProjectName: string | null | undefined,
): number | null => {
  if (urlProject === STANDALONE) return null;
  if (typeof urlProject === 'number') {
    return projects.some((p) => p.id === urlProject) ? urlProject : null;
  }
  return suggestPreviewProject(projects, reportProjectName)?.id ?? null;
};
